import { System, GetSystemsResponse } from 'Services/Api/System/Types';
import SYSTEM_API from 'Services/Api/System/Api';
import { axiosClient } from 'Services/Api/client';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Status } from 'app-types';
import {
	FEATURE_ACCESS_TYPE,
	FEATURE_TYPE
} from 'FeatureManager/FeatureManager.type';
import { getFeatureList } from 'FeatureManager/FeatureManager.service';
import { authStore } from './AuthStore';

type TenantType = { label: string; value: number };
class SystemsStore {
	systems: System[] = [];
	systemsStatusRequest: Status = Status.Idle;
	selectedTenant: TenantType = { label: "sidebar.tenantSelector.noTenantOption", value: 0 };
	rootTenant: TenantType = { label: "sidebar.tenantSelector.noTenantOption", value: 0 };
	tenantList: TenantType[] = [];
	featureList: FEATURE_TYPE[] = [];
	tenantLoading = true;
	featureLoading = false;
	featureAccess: FEATURE_ACCESS_TYPE = {
		training: true,
		phishing: true,
		saqCompliance: true,
		scanning: true
	};
	constructor() {
		makeObservable(this, {
			systems: observable,
			systemsStatusRequest: observable,
			selectedTenant: observable,
			rootTenant: observable,
			tenantList: observable,
			tenantLoading: observable,
			featureList: observable,
			featureLoading: observable,
			featureAccess: observable
		});
	}

	@action
	async loadSystems() {
		try {
			const { data } = await axiosClient.get<GetSystemsResponse>(
				SYSTEM_API.GET_SYSTEMS
			);
			runInAction(() => {
				this.systems = data;
				this.systemsStatusRequest = Status.Success;
			});
		} catch (error) {
			runInAction(() => {
				this.systemsStatusRequest = Status.Failure;
			});
		}
	}

	getSystemByName(systemName: string): System | undefined {
		return this.systems.find((system) => system.systemName === systemName);
	}

	@action
	changeTenant(tenant: TenantType) {
		if (this.selectedTenant.value === tenant.value) return;
		runInAction(() => this.selectedTenant = tenant);
	}

	@action
	setTenants(tenants: TenantType[]) {
		runInAction(() => {
			this.tenantList = tenants;
			this.tenantLoading = false;
		});
	}

	@action
	private async _getFeatureList(tenantId: number) {
		try {
			if (!tenantId) throw new Error('No Tenant Selected.');
			runInAction(() => (this.featureLoading = true));
			const response = await getFeatureList(tenantId);
			this.updateFeatureList(response?.features || []);
		} catch (error) {
			runInAction(() => (this.featureList = []));
		} finally {
			runInAction(() => (this.featureLoading = false));
		}
	}

	@action
	updateFeatureList = (features: FEATURE_TYPE[]) => {
		this.featureList = features;
		const isSuperAdmin = authStore.isCurrentUserSuperAdmin;
		if (isSuperAdmin) return;

		const featureAccess = (Object.keys(this.featureAccess) as Array<
			keyof FEATURE_ACCESS_TYPE
		>).reduce((access, key) => {
			access[key] = features.some(
				(feature) =>
					feature.featureName.replace(/\s/g, '').toLowerCase() ===
					key.toLowerCase() && feature.isEnabled
			);

			return access;
		}, {} as FEATURE_ACCESS_TYPE);

		this.featureAccess = featureAccess;
	}
	
	changeRootTenant(tenant: TenantType) {
		if (this.rootTenant.value === tenant.value) return;
		this._getFeatureList(tenant.value);
		runInAction(() => this.rootTenant = tenant);
	}
}

export const systemsStore = new SystemsStore();

export default SystemsStore;